import React, { useEffect, useState } from 'react';
import { Sidenav, Nav } from 'rsuite';
import 'rsuite/styles/index.less';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom'
import CryptoJS from 'crypto-js';
import { useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import { FaCheckCircle, FaCircle, FaRegCircle, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { useTranslation } from "react-i18next";

// ** Icons Imports
import { Menu } from "react-feather"

// Images
import CheckImage from '../../../assets/images/check.png'
import UnCheckImage from '../../../assets/images/uncheck.png'
import BlueCheckImage from '../../../assets/images/blueCheck.png'

//API-Service
import { progressDimensionListService } from '../../../services/dimensionService'
import { indicatorListService } from '../../../services/indicatorService'
import { sectionQuestionService } from "../../../services/sectionService"

// Utility-Service
import { getToken, getUser } from "../../../utility/AuthService";

// Custom component
import SubmissionEndModal from '../modal/SubmissionEndModal'
import UploadedDocumentsModal from "../modal/UploadedDocumentsModal";
import ShowPdfModal from "../modal/ShowPdfModal";

// Redux
import { selectLanguageUse } from '../../../redux/slices/language.Slice'

// Error Boundary Component
class SidebarErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Sidebar Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="sidebar-error">
          <p>Error loading sidebar content</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Loading Component
const LoadingSpinner = () => (
  <div className="loading-spinner">
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

// Fallback Component for Empty States
const EmptyState = ({ message }) => (
  <div className="empty-state">
    <p>{message}</p>
  </div>
);

// Add loading overlay styles
const loadingOverlayStyles = `
    .loading-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(255, 255, 255, 0.9);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        z-index: 1000;
    }

    .loading-text {
        margin-top: 1rem;
        font-size: 1.1rem;
        color: #666;
    }

    .loading-spinner {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 1rem;
    }
`;

// Add the styles to the document
const styleSheet = document.createElement("style");
styleSheet.innerText = loadingOverlayStyles;
document.head.appendChild(styleSheet);

const SMEReadinessSidebar = (props) => {
    const navigate = useNavigate()
    const { id } = useParams()
    const { updateLoadingState, handleError, logDataFlow } = props;
    const { t } = useTranslation();
    const location = useLocation();

    // Add immediate console log for debugging
    console.log('ProgressSidebar mounted with ID:', id);

    // Move ID decryption into a useEffect to ensure it runs after mount
    const [decryptedSubId, setDecryptedSubId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const decryptId = () => {
            try {
                const id = window.location.pathname.split('/').pop();
                if (!id) {
                    console.error("No ID provided to ProgressSidebar");
                    return;
                }
                console.log("Attempting to decrypt ID:", id);
                const decodedId = decodeURIComponent(id);
                console.log("Decoded ID:", decodedId);
                const decrypted = CryptoJS.AES.decrypt(decodedId, 'mySecretKey').toString(CryptoJS.enc.Utf8);
                console.log("Successfully decrypted ID:", decrypted);
                setDecryptedSubId(decrypted);
            } catch (error) {
                console.error("ID Decryption Error:", error);
                setError(error.message);
            } finally {
                setIsLoading(false);
            }
        };

        decryptId();
    }, []);

    const [allDimension, setAllDimension] = useState([])
    const [allIndicator, setAllIndicator] = useState([])
    const [firstIndicator, setFirstIndicator] = useState([])
    const [expandDimension, setExpandDimension] = useState([])
    const [indicatorID, setIndicatorID] = useState()
    const [currentIndicator, setCurrentIndicator] = useState(null);
    const [currentIndicatorId, setCurrentIndicatorId] = useState(null);
    const [upDocBtn, setUpDocBtn] = useState(false)
    const [modalShow, setModalShow] = useState(false);
    const [uploadedDocumentsModal, setUploadedDocumentsModal] = useState(false);
    const [showPdfModalShow, setShowPdfModalShow] = useState(false);
    const [pdfItem, setPdfItem] = useState();
    const [submissionId, setSubmissionId] = useState();
    const languageUse = useSelector(selectLanguageUse || getUser()?.language)

    // Add section management state
    const [activeSection, setActiveSection] = useState(null);
    const [sectionQuestions, setSectionQuestions] = useState({});

    const indicatorDescriptions = {
        Demographics: {
            Demographics: 'Description for demographics indicator'
        },
        Environment: {
            Water: 'Description for water management',
            Waste: 'Description for waste management'
        },
        Social: {
            'Employee Welfare': 'Description for employee welfare',
            'Community Impact': 'Description for community impact'
        },
        Governance: {
            'Risk Management': 'Description for risk management',
            'Ethics': 'Description for ethics and compliance'
        }
    };

    const handleModalShow = () => {
        setModalShow(!modalShow);
    }

    const handleShowPdfModalShow = () => setShowPdfModalShow(!showPdfModalShow);

    const UploadedDocumentsModaltoggle = (id) => {
        setSubmissionId(id)
        setUploadedDocumentsModal(!uploadedDocumentsModal)
    };

    const loadDimensions = async () => {
        if (!decryptedSubId) {
            console.error('No decrypted ID available for dimensions');
            logDataFlow('No decrypted ID available', null);
            return;
        }

        try {
            setIsLoading(true);
            const startTime = performance.now();
            logDataFlow('Starting dimensions load', { submissionId: decryptedSubId });
            
            const result = await progressDimensionListService({
                authToken: getToken(),
                submissionId: decryptedSubId,
                limit: 100,
                offset: 0
            });

            if (result?.data?.statusCode === 200) {
                setAllDimension(result?.data?.data?.list || []);
                if (result?.data?.data?.list?.length > 0) {
                    const firstDimension = result?.data?.data?.list[0];
                    props.setDimensionName(firstDimension.name);
                    props.setDimensionName_Malay(firstDimension.name_French);
                    props.setCurrentDimension(firstDimension.name);
                    props.setDimensionID(firstDimension._id);
                    setExpandDimension([firstDimension._id]);
                }
            }
        } catch (error) {
            handleError(error, 'loading dimensions');
            logDataFlow('Error loading dimensions', null, error);
        } finally {
            setIsLoading(false);
            updateLoadingState('dimensions', false);
        }
    };

    const loadIndicators = async () => {
        if (!props.dimensionID) {
            logDataFlow('No dimension ID available for indicators', null);
            return;
        }

        try {
            setIsLoading(true);
            const startTime = performance.now();
            logDataFlow('Starting indicators load', { dimensionId: props.dimensionID });
            
            const result = await indicatorListService({
                authToken: getToken(),
                dimension: props.dimensionID,
                limit: 100,
                offset: 0
            });

            if (result?.data?.statusCode === 200) {
                setAllIndicator(result?.data?.data?.list || []);
                if (result?.data?.data?.list?.length > 0) {
                    const firstIndicator = result?.data?.data?.list[0];
                    setCurrentIndicatorId(firstIndicator._id);
                    setCurrentIndicator(firstIndicator);
                    props.setCurrentIndicator(firstIndicator.name);
                    props.setSelectedIndicator({
                        name: firstIndicator.name,
                        dimension: props.currentDimension,
                        description: firstIndicator.description
                    });
                }
            }
        } catch (error) {
            handleError(error, 'loading indicators');
            logDataFlow('Error loading indicators', null, error);
        } finally {
            setIsLoading(false);
            updateLoadingState('indicators', false);
        }
    };

    const loadQuestions = async (indicatorId) => {
        try {
            setIsLoading(true);
            const token = getToken();
            const response = await sectionQuestionService({
                authToken: token,
                indicatorId: indicatorId,
                submissionId: decryptedSubId
            });
            
            if (response?.data?.statusCode === 200) {
                const validatedQuestions = Array.isArray(response.data.data.list) 
                    ? response.data.data.list 
                    : [];
                
                // Update the indicator with its questions
                setAllIndicator(prev => prev.map(indicator => 
                    indicator._id === indicatorId 
                        ? { ...indicator, questions: validatedQuestions }
                        : indicator
                ));
                setError(null);
            } else {
                throw new Error(response?.data?.errorMessage || 'Failed to load questions');
            }
        } catch (err) {
            console.error('Error loading questions:', err);
            setError('Failed to load questions');
            toast.error(t('Failed to load questions'));
        } finally {
            setIsLoading(false);
        }
    };

    // Function to handle section selection
    const handleSectionSelect = (indicatorId, sectionId) => {
        setActiveSection(sectionId);
        // Load questions for the selected section if not already loaded
        if (!sectionQuestions[sectionId]) {
            loadQuestions(indicatorId);
        }
    };

    useEffect(() => {
        if (decryptedSubId) {
            loadDimensions();
        }
    }, [decryptedSubId]);

    useEffect(() => {
        if (props.dimensionID) {
            loadIndicators();
        }
    }, [props.dimensionID]);

    useEffect(() => {
        if (indicatorID) {
            const nextIndicatorIndex = allDimension?.findIndex(item => item._id === indicatorID);
            if (nextIndicatorIndex + 1 < allDimension?.length) {
                const nextIndicator = allDimension[nextIndicatorIndex + 1]?._id;
                loadQuestions(nextIndicator);
            }
        }
    }, [props.changeDimensionName]);

    useEffect(() => {
        if (allDimension?.length > 0 && !expandDimension) {
            // Set initial dimension
            const firstDimension = allDimension[0];
            setExpandDimension(firstDimension?._id);
            props.setDimensionID(firstDimension?._id);
            props?.setCurrentDimension(firstDimension?.name);
            props.setDimensionName(firstDimension?.text);
            props.setDimensionName_Malay(firstDimension?.text_Malay);
            setIndicatorID(firstDimension?._id);
            
            // Set initial indicator
            if (firstDimension) {
                props.setSelectedIndicator({
                    name: firstDimension.name,
                    dimension: firstDimension.name,
                    description: firstDimension.text
                });
            }
        }
    }, [allDimension]);

    useEffect(() => {
        if (allDimension?.length > 0) {
            if (Object.values(allDimension).every(item => item?.isCompleted === 'true')) {
                setUpDocBtn(true)
            }
        }
    }, [allDimension]);

    const calculateDimensionProgress = (dimension) => {
        if (!dimension?.questions || dimension.questions.length === 0) return 0;
        
        const answeredQuestions = dimension.questions.filter(q => 
            q.answer || (Array.isArray(q.yesAnswers) && q.yesAnswers.length > 0)
        ).length;
        
        return Math.round((answeredQuestions / dimension.questions.length) * 100);
    };

    const getIndicatorStatus = indicator => (indicator.completed ? 'completed' : indicator.inProgress ? 'in-progress' : 'not-started');

    const renderIndicatorStatus = status => {
        if (status === 'completed') return <FaCheckCircle />;
        if (status === 'in-progress') return <FaCircle />;
        return <FaRegCircle />;
    };

    // Update chart options text
    const lineOptions = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: 'SME Readiness Trend'
            }
        },
        // ... rest of options
    };

    const getIndicatorDescription = (indicatorName) => {
        for (const dimension of Object.keys(indicatorDescriptions)) {
            if (Object.keys(indicatorDescriptions[dimension]).includes(indicatorName)) {
                return indicatorDescriptions[dimension][indicatorName];
            }
        }
        return '';
    };

    if (isLoading) {
        return (
            <div className="loading-overlay">
                <LoadingSpinner />
                <div className="loading-text">
                    {t('Loading dimensions...')}
                    {t('Loading indicators...')}
                    {t('Loading questions...')}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger">
                {error}
            </div>
        );
    }

    return <SidebarErrorBoundary>
        <div className="progress-sidebar">
            <div className="dimensions-section">
                <h4>{t('Dimensions')}</h4>
                {allDimension?.length > 0 ? (
                    <div className="dimensions-list">
                        {allDimension?.map((dimension, index) => {
                            const progress = calculateDimensionProgress(dimension);
                            const isActive = props.currentDimension === dimension._id;
                            return (
                                <div 
                                    key={dimension._id}
                                    className={`dimension-item ${isActive ? 'active' : ''}`}
                                    onClick={() => {
                                        props.setCurrentDimension(dimension.name);
                                        props.setDimensionID(dimension._id);
                                        setExpandDimension(dimension._id);
                                    }}
                                >
                                    <div className="dimension-info">
                                        <h5 className="dimension-name">
                                            {languageUse === 'English' ? dimension.name : dimension.name_Malay}
                                        </h5>
                                        <div className="progress-bar-container">
                                            <div 
                                                className="progress-bar" 
                                                style={{ width: `${progress}%` }}
                                            />
                                            <span className="progress-text">{progress}%</span>
                                        </div>
                                    </div>
                                    <div className="dimension-stats">
                                        <span className="answered-count">
                                            {dimension.questions?.filter(q => 
                                                q.answer || (Array.isArray(q.yesAnswers) && q.yesAnswers.length > 0)
                                            ).length || 0}
                                        </span>
                                        <span className="total-count">
                                            / {dimension.questions?.length || 0}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                        {!props.viewPage && (
                            <button 
                                className='btn btn-outline-primary mt-4'
                                onClick={() => (
                                    upDocBtn 
                                        ? navigate(`/uploadDocuments/${decryptedSubId}`)
                                        : toast.error('All Questions need to be answered before upload documents')
                                )}
                            >
                                {languageUse === 'English' ? 'Upload Supporting Documents' : 'Muatnaik dokumen sokongan'}
                            </button>
                        )}
                        {props.viewPage && (
                            <button 
                                className='btn btn-outline-primary mt-4'
                                onClick={() => UploadedDocumentsModaltoggle(decryptedSubId)}
                            >
                                {languageUse === 'English' ? 'Upload Supporting Documents' : 'Muatnaik dokumen sokongan'}
                            </button>
                        )}
                    </div>
                ) : (
                    <EmptyState message={t('No dimensions available')} />
                )}
            </div>

            {allIndicator.length > 0 && (
                <div className="indicators-section">
                    <h4>{t('Indicators')}</h4>
                    <div className="indicators-list">
                        {allIndicator.map((indicator) => (
                            <div 
                                key={indicator._id}
                                className={`indicator-item ${getIndicatorStatus(indicator)} ${currentIndicator === indicator.name ? 'active' : ''}`}
                                onClick={() => {
                                    setCurrentIndicator(indicator);
                                    handleSectionSelect(indicator._id, indicator._id);
                                }}
                            >
                                <div className="d-flex align-items-center">
                                    <div className={`status-icon ${getIndicatorStatus(indicator)}`}>
                                        {renderIndicatorStatus(getIndicatorStatus(indicator))}
                                    </div>
                                    <span>
                                        {languageUse === 'English' ? indicator.name : indicator.name_Malay}
                                    </span>
                                </div>
                                {currentIndicator === indicator.name && (
                                    <div className="indicator-details">
                                        <p className="indicator-description text-muted small mt-2 mb-0">
                                            {getIndicatorDescription(indicator.name)}
                                        </p>
                                        {indicator.questions && indicator.questions.length > 0 && (
                                            <div className="questions-preview mt-2">
                                                <small className="text-muted">
                                                    {indicator.questions.length} {t('questions')}
                                                </small>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {allIndicator.length > 0 && (
                <div className="questions-section">
                    <h4>{t('Questions')}</h4>
                    <div className="questions-list">
                        {allIndicator.map((indicator) => (
                            <div 
                                key={indicator._id}
                                className={`question-item ${location.pathname.includes(indicator._id) ? 'active' : ''}`}
                            >
                                <Link
                                    to={`/user/progress/${CryptoJS.AES.encrypt(indicator._id, 'mySecretKey').toString()}`}
                                    className={`btn btn-link ${location.pathname.includes(indicator._id) ? 'active' : ''}`}
                                    onClick={() => handleSectionSelect(indicator._id, indicator._id)}
                                >
                                    {languageUse === 'English' ? indicator.name : indicator.name_Malay}
                                    {indicator.questions && (
                                        <span className="badge bg-secondary ms-2">
                                            {indicator.questions.length}
                                        </span>
                                    )}
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            )}
    </div>
        <SubmissionEndModal 
            show={modalShow} 
            handleClose={handleModalShow} 
            submission_id={decryptedSubId} 
            questionPage={true} 
            decryptedSubId={decryptedSubId} 
        />
        <UploadedDocumentsModal 
            modal={uploadedDocumentsModal} 
            toggle={UploadedDocumentsModaltoggle} 
            handleShowPdfModalShow={handleShowPdfModalShow} 
            submissionId={submissionId} 
            setPdfItem={setPdfItem} 
        />
        <ShowPdfModal 
            show={showPdfModalShow} 
            handleClose={handleShowPdfModalShow} 
            pdfItem={pdfItem} 
        />
    </SidebarErrorBoundary>
};

export default SMEReadinessSidebar